import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Sparkle, ArrowLeft, CheckCircle, FolderOpen } from 'phosphor-react';
import { LESSONS } from '../data/lessons';
import { LESSON_CONTENT } from '../data/lessonContent';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';

const BADGE_COLORS = ['bg-gold', 'bg-nileBlue', 'bg-palmGreen', 'bg-yellow-500'];

const buildUrl = (src) => encodeURI(src);

export const LessonDetail = () => {
  const { id } = useParams();
  const [showAnswers, setShowAnswers] = useState(false);

  const lesson = useMemo(() => LESSONS.find((item) => item.id === id), [id]);
  const content = LESSON_CONTENT[id];

  if (!lesson || !content) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-gold">الدرس غير موجود</h1>
          <p className="text-gray-600">الدرس الذي تبحث عنه غير متاح في الوحدة التجريبية.</p>
          <Link to="/lessons">
            <Button>عودة إلى قائمة الدروس</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <Link to="/lessons" className="text-sm text-gray-500 flex items-center gap-2">
            <ArrowLeft size={16} />
            العودة إلى الدروس
          </Link>
          <h1 className="text-4xl font-bold text-gold flex items-center gap-3">
            <span>{lesson.icon}</span>
            {lesson.title}
          </h1>
          <p className="text-gray-600 max-w-2xl">{content.summary}</p>
          <Link
            to={`/library?lesson=${lesson.id}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-nileBlue underline"
          >
            <FolderOpen size={18} />
            افتح مكتبة الدرس (صور/فيديو/ملفات)
          </Link>
        </div>
        <Card className="relative overflow-hidden w-full md:w-auto">
          <div
            className="absolute inset-x-0 top-0 h-2"
            style={{ backgroundColor: lesson.color }}
          />
          <div className="flex items-center gap-4">
            <div className="text-5xl">{lesson.icon}</div>
            <div className="space-y-1">
              <p className="text-sm text-gray-500">{content.unit}</p>
              <p className="font-bold">{content.grade}</p>
              <p className="text-sm text-gray-500">مدة الدرس: {lesson.duration}</p>
              <p className="text-sm text-gray-500">
                عدد الأسئلة: <span className="number">{lesson.quiz}</span>
              </p>
            </div>
          </div>
        </Card>
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 space-y-4 relative overflow-hidden">
          <div className="absolute -top-6 -left-6 w-16 h-16 bg-desertSand rounded-full opacity-60" />
          <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-gold/20 rounded-full opacity-60" />
          <h2 className="text-2xl font-bold text-nileBlue flex items-center gap-2">
            <Sparkle size={22} />
            أهداف الدرس
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {content.objectives.map((goal, index) => (
              <div
                key={goal}
                className={`rounded-2xl p-3 text-white ${BADGE_COLORS[index % BADGE_COLORS.length]}`}
              >
                {goal}
              </div>
            ))}
          </div>
        </Card>
        <Card className="space-y-3">
          <h2 className="text-2xl font-bold text-nileBlue">معلومات سريعة</h2>
          <div className="flex flex-wrap gap-2">
            {content.facts.map((fact, index) => (
              <span
                key={`${fact}-${index}`}
                className="px-3 py-2 rounded-full bg-desertSand text-sm text-gray-700"
              >
                {fact}
              </span>
            ))}
          </div>
        </Card>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gold">محاور الدرس</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.sections.map((section) => (
            <Card key={section.title} className="space-y-3">
              <h3 className="text-xl font-bold text-nileBlue">{section.title}</h3>
              {section.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-gray-600">
                  {paragraph}
                </p>
              ))}
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                {section.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gold">المعجم الصغير</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {content.glossary.map((item) => (
            <Card key={item.term} className="space-y-2">
              <p className="text-lg font-bold text-nileBlue">{item.term}</p>
              <p className="text-sm text-gray-600">{item.definition}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h2 className="text-2xl font-bold text-gold">أسئلة الاختيار من متعدد</h2>
          <Button variant="secondary" onClick={() => setShowAnswers((prev) => !prev)}>
            {showAnswers ? 'إخفاء الإجابات' : 'إظهار الإجابات'}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {content.questions.mcq.map((question, index) => (
            <Card key={`${question.q}-${index}`} className="space-y-3">
              <div className="flex items-start gap-2">
                <span className="text-gold font-bold">س{index + 1}</span>
                <p className="font-semibold">{question.q}</p>
              </div>
              <div className="space-y-2">
                {question.options.map((option, optionIndex) => {
                  const isCorrect = showAnswers && optionIndex === question.answer;
                  return (
                    <div
                      key={`${question.q}-${option}`}
                      className={`flex items-center gap-2 p-2 rounded-xl border ${
                        isCorrect
                          ? 'border-palmGreen bg-palmGreen/10 text-palmGreen'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      {isCorrect && <CheckCircle size={16} weight="fill" />}
                      <span className="text-sm">{option}</span>
                    </div>
                  );
                })}
              </div>
              {showAnswers && (
                <p className="text-sm text-gray-600">الإجابة: {question.explanation}</p>
              )}
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gold">أسئلة قصيرة للتفكير</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.questions.short.map((question, index) => (
            <Card key={`${question.q}-${index}`} className="space-y-2">
              <p className="font-semibold">سؤال {index + 1}:</p>
              <p className="text-gray-700">{question.q}</p>
              {showAnswers && (
                <p className="text-sm text-gray-600">إجابة مقترحة: {question.answer}</p>
              )}
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gold">أنشطة تطبيقية</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.activities.map((activity, index) => (
            <Card key={`${activity}-${index}`} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-desertSand flex items-center justify-center font-bold">
                {index + 1}
              </div>
              <p className="text-gray-700">{activity}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-gold">صور ومواد إثرائية</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {content.media.map((item, index) => (
            <Card key={`${item.src}-${index}`} className="space-y-3">
              <img
                src={buildUrl(item.src)}
                alt={item.caption}
                className="w-full h-44 object-cover rounded-xl"
                loading="lazy"
              />
              <p className="text-sm text-gray-600">{item.caption}</p>
            </Card>
          ))}
        </div>
      </section>

      <Card className="text-center space-y-4 bg-gradient-to-r from-gold/90 to-yellow-400 text-white">
        <h2 className="text-3xl font-bold">مراجعة سريعة</h2>
        <p className="text-lg">
          اقرأ المحاور، ثم جرّب الإجابة عن الأسئلة. لا تنسَ أن كل سؤال يساعدك على أن تصبح مستكشفًا أقوى!
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <span className="bg-white/20 px-4 py-2 rounded-full">⭐ تعلم</span>
          <span className="bg-white/20 px-4 py-2 rounded-full">🧠 فكر</span>
          <span className="bg-white/20 px-4 py-2 rounded-full">🗺️ اكتشف</span>
          <span className="bg-white/20 px-4 py-2 rounded-full">✅ راجع</span>
        </div>
      </Card>
    </div>
  );
};
